import { css } from "@emotion/react";
import { endOfDay, isDate, startOfDay } from "date-fns";
import * as React from "react";

import { IPickerListItem } from "@livechat/design-system-react-components";

import { ControlledFilter } from "components/filter";
import { FilterButton } from "components/filter/FilterButton";
import { ICustomRangeDatePickerOption } from "components/filters/date-range-filter/interfaces";
import { CustomDateRangeFilterId, Filter, IDateRangeWithCompareFilter } from "constants/filters";

import { SelectedFilterLabel } from "../SelectedFilterLabel";
import { IFilterBaseProps, IFilterConfig, IFilterUpdate } from "../interfaces";

import { DateRangeFilterControls } from "./DateRangeFilterControls";
import { getItems } from "./date-range-filter";

export const getTimeFrameLetter = (timeFrame: Date | string): string =>
  (timeFrame && timeFrame.toString().match(/[A-Z]/g)?.[0]) || "";

export const getNumericValue = (value: string): number =>
  !isNaN(Math.abs(parseInt(value, 10))) ? Math.abs(parseInt(value, 10)) : 0;

export const getTimeFrame = (timeFrame: Date | string): string => {
  const timeFrameLetter = getTimeFrameLetter(timeFrame);
  const numericValue = getNumericValue(timeFrame.toString());

  if (numericValue === 0) {
    switch (timeFrameLetter) {
      case "N":
        return "minute";
      case "H":
        return "hour";
      case "D":
        return "day";
      default:
        return "...";
    }
  } else {
    switch (timeFrameLetter) {
      case "N":
        return "minutes";
      case "H":
        return "hours";
      case "D":
        return "days";
      default:
        return "...";
    }
  }
};

export const filterBoxStyles = css`
  width: 480px;
`;

export const filterButtonStyles = css`
  max-width: 128px;
  margin-left: auto;
`;

type IProps = {
  filterName: Filter;
  filterValues: IDateRangeWithCompareFilter;
  filterConfig: IFilterConfig;
  isOpen: boolean;
  onChange: (filterUpdate: IFilterUpdate<IDateRangeWithCompareFilter | null>) => void;
  onClose(): void;
  skipCustomPeriodOption?: boolean;
} & IFilterBaseProps<Filter>;

interface IState {
  numericValue: string;
  timeFrame: string;
  selectedOption: IPickerListItem[] | null;
}

class DateRangeFilter extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    const { filterValues, skipCustomPeriodOption } = props;
    const selected = getItems(skipCustomPeriodOption).find(item => item.key === (filterValues && filterValues.id));

    this.state = {
      numericValue: "1",
      timeFrame: "N",
      selectedOption: selected ? [selected] : null,
    };
  }

  componentDidMount(): void {
    const { filterValues } = this.props;
    if (filterValues && filterValues.from) {
      this.setState({
        numericValue: getNumericValue(filterValues.from as string).toString(10),
        timeFrame: getTimeFrameLetter(filterValues.from),
      });
    }
  }

  componentDidUpdate(prevProps: IProps): void {
    if (prevProps.filterValues !== this.props.filterValues) {
      const selected = getItems(this.props.skipCustomPeriodOption).find(
        item => item.key === (this.props.filterValues && this.props.filterValues.id)
      );
      if (selected) {
        this.setState({ selectedOption: [selected] });
      }
    }
    if (prevProps.isOpen && !this.props.isOpen) {
      this.props.onChange({
        name: this.props.filterName,
        value: null,
        isDraft: true,
      });
    }
  }

  handleChange = (option: ICustomRangeDatePickerOption): void => {
    if (option === null) {
      this.props.onChange({
        name: this.props.filterName,
        value: null,
      });
      this.props.onClose();
    } else {
      const {
        value: { from, to },
        id,
      } = option;

      const fromDate = isDate(option.value.from) ? startOfDay(option.value.from) : undefined;
      const toDate = isDate(option.value.to) ? endOfDay(option.value.to) : undefined;

      const filterValue: IDateRangeWithCompareFilter = {
        id: option.id,
        from: option.relativeValue ? option.relativeValue.from : fromDate,
        to: option.relativeValue ? option.relativeValue.to : toDate,
      };

      const isDraft =
        (id === CustomDateRangeFilterId.CustomPeriod || id === CustomDateRangeFilterId.CustomTime) && (!from || !to);
      this.props.onChange({
        name: this.props.filterName,
        value: filterValue,
        isDraft,
      });
      if (!isDraft) {
        this.props.onClose();
      }
    }
  };

  handleTimeFrame = (timeFrame: string): void => {
    this.setState({ ...this.state, timeFrame });
  };

  handleNumericValue = (numericValue: string): void => {
    this.setState({ ...this.state, numericValue });
  };

  handleRemove = (): void => {
    this.props.onRemove(this.props.filterName);
  };

  handleOpen = (): void => {
    this.props.onOpen(this.props.filterName);
  };

  handleFilterRemoveClick = (event: React.MouseEvent<HTMLSpanElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    this.handleRemove();
  };

  handleActionButtonClick = (): void => {
    const {
      filterValues: { id },
      onClose,
      onChange,
      filterName,
    } = this.props;
    if (id === CustomDateRangeFilterId.CustomTime) {
      onChange({
        name: filterName,
        value: { from: `-${parseInt(this.state.numericValue, 10) - 1}${this.state.timeFrame}`, to: undefined, id },
      });
    }
    onClose();
  };

  handleSelectedOption = (selectedOption: IPickerListItem[] | null): void => {
    this.setState({ ...this.state, selectedOption });
  };

  render(): React.ReactNode {
    const { title, icon, removable = true } = this.props.filterConfig;

    const { filterValues, filterLabelValues, isOpen, onClose, filterName, skipCustomPeriodOption } = this.props;
    const valueIsEmpty = filterValues === null;
    const invalid = Array.isArray(filterLabelValues) && filterLabelValues.find(l => l.invalid) !== undefined;
    const TodayDate = new Date();
    TodayDate.setHours(0, 0, 0, 0);
    return (
      <ControlledFilter
        title={title}
        boxCss={filterValues?.id === CustomDateRangeFilterId.CustomPeriod ? filterBoxStyles : undefined}
        icon={icon}
        isOpen={isOpen}
        isValid
        actionButtonText={valueIsEmpty ? "Close" : "Done"}
        onActionButtonClick={this.handleActionButtonClick}
        onClose={onClose}
        onOpen={this.handleOpen}
        onFilterRemoveClick={this.handleRemove}
        renderContent={() =>
          isOpen && (
            <DateRangeFilterControls
              setSelectedOption={this.handleSelectedOption}
              selectedOption={this.state.selectedOption}
              filterName={filterName}
              isOpen={isOpen}
              onClose={onClose}
              onChange={this.handleChange}
              filterValues={filterValues}
              numericValue={this.state.numericValue}
              onHandleNumericvalue={this.handleNumericValue}
              timeFrame={this.state.timeFrame}
              onHandleTimeFrame={this.handleTimeFrame}
              skipCustomPeriodOption={skipCustomPeriodOption}
            />
          )
        }
      >
        <FilterButton
          title={title}
          icon={icon}
          removable={removable}
          selectedValuesLabel={<SelectedFilterLabel value={this.props.filterLabelValues} />}
          invalid={invalid}
          onRemove={this.handleFilterRemoveClick}
          shouldHideTitle={!valueIsEmpty}
        />
      </ControlledFilter>
    );
  }
}

export default DateRangeFilter;
